import React from 'react';
import { Space, Button, Typography, Collapse, Tag, InputNumber, Select, Slider, Alert } from 'antd';
import { 
  AudioOutlined, 
  ReloadOutlined,
  CustomerServiceOutlined,
  SettingOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { IsolatedVideoPlayer } from '../../../features/video-editor/components/IsolatedVideoPlayer';
import { EnhancedPlayerControls } from '../../../components/EnhancedPlayerControls';
import { ProfessionalTimeline } from '../../../components/ProfessionalTimeline';
import { SourceEpisode, AudioAsset } from '../../../types';
import { SimpleVideoPlayerHandle } from '../../../features/video-editor/components/SimpleVideoPlayer';

const { Text } = Typography;
const { Panel: CollapsePanel } = Collapse;

interface VideoEditorLeftPanelProps {
  // Player props
  playerRef: React.RefObject<SimpleVideoPlayerHandle | null>;
  videoEditorPanels: any[];
  panelDurations: number[];
  episode: SourceEpisode;
  backgroundMusicUrl?: string;
  editedBgmVolume: number;
  aspectRatio: '16:9' | '9:16';
  mutedTracks: { voice: boolean; bgm: boolean; sfx: boolean };
  playbackSpeed: number;
  audioDuration: number | null;
  hasTimestamps: boolean;
  
  // Subtitle props
  showSubtitles: boolean;
  currentSubtitle: string;
  setShowSubtitles: (show: boolean) => void;
  selectedSubtitleLang: string;
  setSelectedSubtitleLang: (lang: string) => void;
  
  // Controls props
  isPlaying: boolean;
  handlePlayPause: () => void;
  handlePrevPanel: () => void;
  handleNextPanel: () => void;
  currentTime: number;
  totalDuration: number;
  handleSeek: (time: number) => void;
  setPlaybackSpeed: (speed: number | null) => void;
  handleToggleMute: (track: 'voice' | 'bgm' | 'sfx') => void;
  
  // Timeline props
  editedPanels: any[];
  selectedPanelId: string | null;
  setSelectedPanelId: (id: string | null) => void;
  handleMotionSelect: (panelId: string, index: number) => void;
  handleSoundSelect: (panelId: string, index: number) => void;
  handleSfxTimelineUpdate: (update: any) => void;

  // AI & Action props
  currentStep: number;
  hasMotion: boolean;
  hasAudio: boolean;
  localEpisode: SourceEpisode;
  getAudioStatusTag: (status: string | undefined) => React.ReactNode;
  chunkSize: number;
  setChunkSize: (size: number) => void;
  handleProcessAudio: () => void;
  isProcessingAudio: boolean;
  handleAnalyzeMotionOnly: () => void;
  handleAnalyzeMusicOnly: () => void;
  handleAnalyzeSfxOnly: () => void;
  isAnalyzingEffects: boolean;
  
  // BGM props
  editedBgmId?: string;
  handleBgmChange: (value: string | undefined) => void;
  musicList: AudioAsset[];
  handleBgmVolumeChange: (value: number | null) => void;
  refreshAudioLibrary: () => void;

  // Video Generation props
  isGeneratingVideo: boolean;
  videoStatus: string;
  renderProgress: number;
  handleGenerateVideo: () => void;
}

const VideoEditorLeftPanel: React.FC<VideoEditorLeftPanelProps> = ({
  playerRef,
  videoEditorPanels,
  panelDurations,
  episode,
  backgroundMusicUrl,
  editedBgmVolume,
  aspectRatio,
  mutedTracks,
  playbackSpeed,
  audioDuration,
  hasTimestamps,
  showSubtitles,
  currentSubtitle,
  setShowSubtitles,
  selectedSubtitleLang,
  setSelectedSubtitleLang,
  isPlaying,
  handlePlayPause,
  handlePrevPanel,
  handleNextPanel,
  currentTime,
  totalDuration,
  handleSeek,
  setPlaybackSpeed,
  handleToggleMute,
  editedPanels,
  selectedPanelId,
  setSelectedPanelId,
  handleMotionSelect,
  handleSoundSelect,
  handleSfxTimelineUpdate,
  currentStep,
  hasMotion,
  hasAudio,
  localEpisode,
  getAudioStatusTag,
  chunkSize,
  setChunkSize,
  handleProcessAudio,
  isProcessingAudio,
  handleAnalyzeMotionOnly,
  handleAnalyzeMusicOnly,
  handleAnalyzeSfxOnly,
  isAnalyzingEffects,
  editedBgmId,
  handleBgmChange,
  musicList,
  handleBgmVolumeChange,
  refreshAudioLibrary,
  isGeneratingVideo,
  videoStatus,
  renderProgress,
  handleGenerateVideo,
}) => {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* CENTER: Video Preview + Timeline */}
        <div style={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#1a1a1a',
          overflow: 'hidden'
        }}>
          {/* Video Preview */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            padding: '24px',
            minHeight: 0,
            position: 'relative'
          }}>
            <IsolatedVideoPlayer
              ref={playerRef}
              panels={videoEditorPanels}
              panelDurations={panelDurations}
              audioPath={episode.masterAudioUrl}
              backgroundMusicUrl={backgroundMusicUrl}
              backgroundVolume={editedBgmVolume}
              aspectRatio={aspectRatio}
              mutedTracks={mutedTracks}
              playbackSpeed={playbackSpeed}
              audioDuration={audioDuration}
              hasTimestamps={hasTimestamps}
            />
            
            {showSubtitles && currentSubtitle && (
              <div style={{
                position: 'absolute',
                bottom: '12%',
                left: '50%',
                transform: 'translateX(-50%)',
                color: '#FFFFFF',
                padding: '4px 12px',
                fontSize: '16px',
                fontFamily: 'Arial, sans-serif',
                textAlign: 'center',
                maxWidth: '85%',
                zIndex: 10,
                pointerEvents: 'none',
                lineHeight: '1.6',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.9), -1px -1px 2px rgba(0, 0, 0, 0.9), 1px -1px 2px rgba(0, 0, 0, 0.9), -1px 1px 2px rgba(0, 0, 0, 0.9)',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontWeight: '600'
              }}>
                {currentSubtitle}
              </div>
            )}
          </div>

          {/* Player Controls */}
          <div style={{ backgroundColor: '#1e1e1e', borderTop: '1px solid #3a3a3a' }}>
            <EnhancedPlayerControls
              isPlaying={isPlaying}
              onPlayPause={handlePlayPause}
              onPrevPanel={handlePrevPanel}
              onNextPanel={handleNextPanel}
              currentTime={currentTime}
              totalDuration={totalDuration}
              onSeek={handleSeek}
              mutedTracks={mutedTracks}
              onToggleMute={handleToggleMute}
              playbackSpeed={playbackSpeed}
              onSpeedChange={setPlaybackSpeed}
              subtitle={
                episode.subtitles && (episode.subtitles as any).files && Object.keys((episode.subtitles as any).files).length > 0
                  ? {
                      show: showSubtitles,
                      onToggle: setShowSubtitles,
                      lang: selectedSubtitleLang,
                      onLangChange: setSelectedSubtitleLang,
                      languages: Object.keys((episode.subtitles as any).files).map(lang => {
                        const file = (episode.subtitles as any).files[lang];
                        return { value: lang, label: `${file.languageName || lang} (${file.entriesCount || 0})` };
                      }),
                    }
                  : undefined
              }
            />
          </div>

          {/* Timeline */}
          {hasTimestamps && (
            <div style={{ 
              height: '300px',
              backgroundColor: '#2a2a2a',
              borderTop: '1px solid #3a3a3a',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <ProfessionalTimeline
                panels={editedPanels.map((pm) => ({
                  panelId: pm.panelId,
                  orderIndex: pm.orderIndex,
                  startTime: pm.startTime,
                  endTime: pm.endTime,
                  imageUrl: pm.imageUrl,
                  caption: pm.caption,
                  motionEffects: pm.motionEffects || [],
                  soundEffects: pm.soundEffects || [],
                }))}
                totalDuration={totalDuration}
                currentTime={currentTime}
                selectedPanelId={selectedPanelId}
                onPanelSelect={setSelectedPanelId}
                onSeek={handleSeek}
                backgroundMusicUrl={backgroundMusicUrl}
                onMotionSelect={handleMotionSelect}
                onSoundSelect={handleSoundSelect}
                onSfxTimelineUpdate={handleSfxTimelineUpdate}
              />
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR Content (Partially extracted to LeftPanel for functional grouping as requested) */}
        <div style={{ 
          width: '22%',
          minWidth: '300px',
          backgroundColor: '#2a2a2a',
          borderLeft: '1px solid #3a3a3a',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '12px'
          }}>
            <Collapse 
              defaultActiveKey={['settings', 'ai']} 
              style={{ 
                backgroundColor: '#333',
                border: '1px solid #3a3a3a'
              }}
            >
              <CollapsePanel
                header={<Text style={{ color: '#fff' }}><SettingOutlined style={{ marginRight: 8 }} />⚙️ Episode Settings</Text>}
                key="settings"
                style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
              >
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ color: '#999', fontSize: '11px', display: 'block', marginBottom: 8 }}>
                    Playback Speed Multiplier (Preview & Export)
                  </Text>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Slider
                      min={0.5}
                      max={2.0}
                      step={0.05}
                      value={playbackSpeed}
                      onChange={setPlaybackSpeed}
                      style={{ flex: 1 }}
                    />
                    <InputNumber
                      min={0.5}
                      max={2.0}
                      step={0.05}
                      value={playbackSpeed}
                      onChange={setPlaybackSpeed}
                      formatter={(value) => `${value}x`}
                      parser={(value) => parseFloat(value?.replace('x', '') || '1')}
                      style={{ width: 70 }}
                      size="small"
                    />
                  </div>
                </div>
              </CollapsePanel>

              <CollapsePanel
                header={<Text style={{ color: '#fff' }}>🤖 AI Analysis</Text>}
                key="ai"
                style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div style={{ marginBottom: 12 }}>
                    <Text style={{ color: '#fff', fontSize: '13px', display: 'block', marginBottom: 8 }}>
                      🎵 Process Audio (Gen + Analyze)
                    </Text>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text style={{ color: '#999', fontSize: '11px' }}>Status:</Text>
                        {getAudioStatusTag(localEpisode.audioStatus)}
                      </div>
                      {localEpisode.audioChunks && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text style={{ color: '#999', fontSize: '11px' }}>Progress:</Text>
                          <Tag color="blue">
                            {localEpisode.audioChunks.filter((c: any) => c.status === 'done').length}/{localEpisode.audioChunks.length} chunks done
                          </Tag>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                        <Text style={{ color: '#999', fontSize: '11px' }}>Chunk size:</Text>
                        <InputNumber
                          min={5}
                          max={50}
                          step={1}
                          value={chunkSize}
                          onChange={(val) => setChunkSize(val || 10)}
                          size="small"
                          style={{ width: 60 }}
                        />
                      </div>
                      <Button
                        type="primary"
                        icon={<AudioOutlined />}
                        onClick={handleProcessAudio}
                        loading={isProcessingAudio}
                        block
                        size="middle"
                      >
                        {localEpisode.audioStatus === 'ready' ? '🔄 Re-process Audio' : 'Process Audio'}
                      </Button>
                    </Space>
                  </div>

                  {hasTimestamps && (
                    <>
                      <div style={{ borderTop: '1px solid #444', margin: '12px 0 8px 0', paddingTop: '12px' }}>
                        <Text style={{ color: '#999', fontSize: '11px', display: 'block', marginBottom: 8 }}>
                          📝 Step 2: AI Effects Analysis
                        </Text>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Button
                          icon={<ThunderboltOutlined />}
                          onClick={() => handleAnalyzeMotionOnly()}
                          loading={isAnalyzingEffects}
                          block
                        >
                          Analyze Motion
                        </Button>
                        <Button
                          icon={<CustomerServiceOutlined />}
                          onClick={() => handleAnalyzeMusicOnly()}
                          loading={isAnalyzingEffects}
                          block
                        >
                          Analyze Music
                        </Button>
                        <Button
                          icon={<AudioOutlined />}
                          onClick={() => handleAnalyzeSfxOnly()}
                          loading={isAnalyzingEffects}
                          block
                        >
                          Analyze SFX
                        </Button>
                      </div>
                    </>
                  )}
                </Space>
              </CollapsePanel>
            </Collapse>

            <Collapse 
              defaultActiveKey={['bgm']} 
              style={{ marginTop: 12, backgroundColor: '#333', border: '1px solid #3a3a3a' }}
            >
              <CollapsePanel
                header={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: '#fff' }}>🎵 Background Music</Text>
                    <Button
                      type="text"
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={(e) => { e.stopPropagation(); refreshAudioLibrary(); }}
                      style={{ color: '#1890ff' }}
                    />
                  </div>
                }
                key="bgm"
                style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Select
                    value={editedBgmId}
                    onChange={handleBgmChange}
                    placeholder="Select background music"
                    showSearch
                    style={{ width: '100%' }}
                    size="small"
                    allowClear
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={musicList.map((music: AudioAsset) => ({
                      value: music.id,
                      label: `${music.name} ${music.mood?.join(', ') ? `(${music.mood.join(', ')})` : ''}`
                    }))}
                  />
                  {editedBgmId && (
                    <div>
                      <Text style={{ fontSize: '11px', display: 'block', marginBottom: 4, color: '#999' }}>
                        Volume:
                      </Text>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={editedBgmVolume}
                        onChange={handleBgmVolumeChange}
                        marks={{
                          0: { label: <span style={{ color: '#999' }}>0</span> },
                          0.25: { label: <span style={{ color: '#999' }}>0.25</span> },
                          0.5: { label: <span style={{ color: '#999' }}>0.5</span> },
                          1: { label: <span style={{ color: '#999' }}>1</span> }
                        }}
                      />
                    </div>
                  )}
                </Space>
              </CollapsePanel>
            </Collapse>

            {(episode.videoUrl || (isGeneratingVideo && videoStatus !== 'not_generated')) && (
              <Collapse 
                defaultActiveKey={['video']} 
                style={{ marginTop: 12, backgroundColor: '#333', border: '1px solid #3a3a3a' }}
              >
                <CollapsePanel
                  header={<Text style={{ color: '#fff' }}>🎬 Rendered Video</Text>}
                  key="video"
                  style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {isGeneratingVideo && videoStatus !== 'completed' && (
                      <div>
                        <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                          Status: {videoStatus === 'queued' ? '⏳ Queued' : 
                                  videoStatus === 'processing' ? '🎬 Rendering' : 
                                  videoStatus === 'failed' ? '❌ Failed' : videoStatus}
                        </Text>
                        <div style={{ width: '100%', height: '4px', backgroundColor: '#444', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ width: `${renderProgress}%`, height: '100%', backgroundColor: '#52c41a', transition: 'width 0.3s ease' }} />
                        </div>
                        <Text style={{ fontSize: '10px', color: '#999', marginTop: 4 }}>{renderProgress}%</Text>
                      </div>
                    )}
                    {episode.videoUrl && (
                      <div style={{ padding: '8px', backgroundColor: '#1a1a1a', borderRadius: '4px', border: '1px solid #444' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text style={{ fontSize: '11px', color: '#52c41a' }}>✓ Video ready</Text>
                          <a href={episode.videoUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold' }}>🎬 Open Video →</a>
                        </div>
                        {episode.subtitles && (episode.subtitles as any).files && Object.keys((episode.subtitles as any).files).length > 0 && (
                          <div style={{ borderTop: '1px solid #333', paddingTop: '8px' }}>
                            <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>📝 Subtitles:</Text>
                            {Object.entries((episode.subtitles as any).files).map(([lang, file]: [string, any]) => (
                              <div key={lang} style={{ display: 'flex', gap: '12px', marginBottom: 4 }}>
                                <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', color: '#1890ff' }}>{file.languageName} (VTT)</a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {videoStatus === 'failed' && (
                      <Alert message="Render Failed" description="Video rendering failed. Please try again." type="error" showIcon style={{ fontSize: '11px' }} />
                    )}
                  </Space>
                </CollapsePanel>
              </Collapse>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoEditorLeftPanel;
